import { CloseOutlined, PaperClipOutlined } from '@ant-design/icons'
import {
  EmptyState,
  ExpandableCard,
  type ExpandableCardField,
  Heading,
  NavigationModal,
  Spinner
} from '@lmc/ui-kit'
import type { ReferralResponse } from '@lmc/validations'
import type { MenuProps } from 'antd'
import { NAMESPACE } from 'locale'
import { referralConstants } from 'locale/referrals'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { formatDate, formatDateTime } from '../utils/referralUtils'

type Referral = ReferralResponse

import { UserRole } from '@lmc/ts-common'
import useRoleAccess from '../../../../../components/AccessControl/useRoleAccess'
import { useInfiniteScrollFetch } from '../../../../../hooks/useInfiniteScroll'
import { useReferralArchive } from '../hooks/useReferralArchive'
import { useReferralAttachmentRemove } from '../hooks/useReferralAttachmentRemove'

interface ReferralListProps {
  referrals: Referral[]
  isLoading: boolean
  onEdit: (referral: Referral) => void
  onRefetch: () => void
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

const STATUS_TAG_COLORS: Record<string, string> = {
  active: 'green',
  archived: 'default'
}

export const ReferralList: React.FC<ReferralListProps> = ({
  referrals,
  isLoading,
  onEdit,
  onRefetch,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage
}) => {
  const { t } = useTranslation(NAMESPACE.REFERRALS)
  const { id: residentId } = useParams<{ id: string }>()
  const [expandedReferralId, setExpandedReferralId] = useState<string | null>(
    null
  )
  const [archiveModalVisible, setArchiveModalVisible] = useState(false)
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(
    null
  )

  const { archiveReferral, unarchiveReferral } = useReferralArchive(
    residentId || '',
    () => {
      onRefetch()
      setArchiveModalVisible(false)
      setSelectedReferral(null)
    }
  )

  const { removeAttachment } = useReferralAttachmentRemove({
    residentId: residentId || '',
    onSuccess: () => {
      onRefetch()
    }
  })

  const [showRemoveAttachmentModal, setShowRemoveAttachmentModal] =
    useState(false)
  const [attachmentToRemove, setAttachmentToRemove] = useState<{
    referralId: string
    attachmentId: string
    filename: string
  } | null>(null)

  const { infiniteRef, rootRef } = useInfiniteScrollFetch({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  })

  const hasEditAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Referral'
      }
    ]
  )

  const hasArchiveAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'delete',
        this: 'Referral'
      }
    ]
  )

  if (isLoading) {
    return <Spinner />
  }

  if (referrals.length === 0) {
    return (
      <EmptyState
        title={t(referralConstants.NO_REFERRALS_FOUND)}
        description={t(referralConstants.NO_REFERRALS_DESCRIPTION)}
      />
    )
  }

  const activeReferrals = referrals.filter((r) => r.status === 'active')
  const archivedReferrals = referrals.filter((r) => r.status === 'archived')

  const toggleExpand = (referralId: string) => {
    setExpandedReferralId(expandedReferralId === referralId ? null : referralId)
  }

  const handleArchiveClick = (referral: Referral) => {
    setSelectedReferral(referral)
    setArchiveModalVisible(true)
  }

  const handleConfirmArchive = async () => {
    if (!selectedReferral) return

    if (selectedReferral.status === 'active') {
      await archiveReferral(selectedReferral._id)
    } else {
      await unarchiveReferral(selectedReferral._id)
    }
  }

  const getMenuItems = (referral: Referral): MenuProps['items'] => {
    const items: MenuProps['items'] = []

    if (hasEditAccess && referral.status === 'active') {
      items.push({
        key: 'edit',
        label: t(referralConstants.EDIT),
        onClick: () => onEdit(referral)
      })
    }

    if (hasArchiveAccess) {
      items.push({
        key: 'archive',
        label:
          referral.status === 'active'
            ? t(referralConstants.ARCHIVE)
            : t(referralConstants.UN_ARCHIVE),
        onClick: () => handleArchiveClick(referral)
      })
    }

    return items
  }

  const getStatusLabel = (status: string) => {
    return status === 'active'
      ? t(referralConstants.ACTIVE)
      : t(referralConstants.ARCHIVED)
  }

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const handleConfirmRemoveAttachment = async () => {
    if (!attachmentToRemove) return

    try {
      await removeAttachment(
        attachmentToRemove.referralId,
        attachmentToRemove.attachmentId
      )
      setShowRemoveAttachmentModal(false)
      setAttachmentToRemove(null)
    } catch (error) {
      console.error('Failed to remove attachment:', error)
    }
  }

  const handleCancelRemoveAttachment = () => {
    setShowRemoveAttachmentModal(false)
    setAttachmentToRemove(null)
  }

  const buildFields = (referral: Referral): ExpandableCardField[] => {
    const fields: ExpandableCardField[] = []

    if (referral.referredTo) {
      fields.push({
        label: t(referralConstants.REFERRED_TO),
        value: referral.referredTo,
        fullWidth: true
      })
    }

    fields.push({
      label: t(referralConstants.REASON_FOR_REFERRAL),
      value: referral.reasonForReferral,
      fullWidth: true
    })

    if (referral.attachments && referral.attachments.length > 0) {
      // Create clickable links for each attachment as React components
      const attachments = referral.attachments
      const attachmentLinks = attachments.map((attachment, index) => {
        const url = attachment.url || ''
        const filename = attachment.filename || `Attachment ${index + 1}`
        const attachmentId =
          (attachment as any)._id ||
          (attachment as any).id ||
          attachment.filename
        return (
          <DocumentRow
            key={index}
            style={{
              marginBottom: index < attachments.length - 1 ? '4px' : 0
            }}
          >
            <AttachmentLink
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {filename}
            </AttachmentLink>
            {hasEditAccess && referral.status === 'active' && (
              <RemoveButton
                onClick={(e) => {
                  e.stopPropagation()
                  setAttachmentToRemove({
                    referralId: referral._id,
                    attachmentId,
                    filename
                  })
                  setShowRemoveAttachmentModal(true)
                }}
                title="Remove attachment"
              >
                <CloseOutlined />
              </RemoveButton>
            )}
          </DocumentRow>
        )
      })

      fields.push({
        label: t(referralConstants.ATTACHMENTS),
        value: <>{attachmentLinks}</>,
        fullWidth: true
      })
    }

    if (referral.createdBy?.name) {
      fields.push({
        label: t(referralConstants.CREATED_BY),
        value: `${referral.createdBy.name}${
          referral.createdAt ? ` on ${formatDateTime(referral.createdAt)}` : ''
        }`,
        fullWidth: false
      })
    }

    if (referral.updatedBy?.name) {
      fields.push({
        label: t(referralConstants.UPDATED_BY),
        value: `${referral.updatedBy.name}${
          referral.updatedAt ? ` on ${formatDateTime(referral.updatedAt)}` : ''
        }`,
        fullWidth: false
      })
    }

    if (referral.archivedBy?.name) {
      fields.push({
        label: t(referralConstants.ARCHIVED_BY),
        value: `${referral.archivedBy.name}${
          referral.archivedAt
            ? ` on ${formatDateTime(referral.archivedAt)}`
            : ''
        }`,
        fullWidth: false
      })
    }

    return fields
  }

  const renderReferral = (referral: Referral) => {
    const dateText = formatDate(referral.referralDate)
    const menuItems = getMenuItems(referral)
    const statusTagColor =
      STATUS_TAG_COLORS[referral.status] || STATUS_TAG_COLORS.active
    const fields = buildFields(referral)

    // Add attachment icon if there are attachments
    const hasAttachments =
      referral.attachments && referral.attachments.length > 0
    const attachmentCount = referral.attachments?.length || 0

    return (
      <ExpandableCard
        key={referral._id}
        id={referral._id}
        title={truncateText((referral as any).title || '', 60)}
        titleIndicators={
          hasAttachments ? (
            <AttachmentIcon title={t(referralConstants.ATTACHMENTS)}>
              <PaperClipOutlined />
              {attachmentCount > 1 && (
                <span style={{ marginLeft: '4px' }}>({attachmentCount})</span>
              )}
            </AttachmentIcon>
          ) : undefined
        }
        status={
          referral.status === 'archived'
            ? {
                label: getStatusLabel(referral.status),
                color: statusTagColor
              }
            : undefined
        }
        date={
          dateText
            ? `Referred: ${dateText}`
            : t(referralConstants.NO_DATE_SPECIFIED)
        }
        fields={fields}
        menuItems={menuItems}
        isExpanded={expandedReferralId === referral._id}
        onToggleExpand={fields.length > 0 ? toggleExpand : undefined}
        testId={`referral-${referral._id}`}
      />
    )
  }

  return (
    <>
      <ScrollContainer ref={rootRef}>
        <ListContainer>
          {activeReferrals.map(renderReferral)}

          {archivedReferrals.length > 0 && (
            <>
              <SectionHeading>
                <Heading level={4}>
                  {t(referralConstants.ARCHIVED_REFERRALS)}
                </Heading>
              </SectionHeading>
              {archivedReferrals.map(renderReferral)}
            </>
          )}

          {(hasNextPage || isFetchingNextPage) && (
            <LoadMoreContainer ref={infiniteRef}>
              {isFetchingNextPage && <Spinner />}
            </LoadMoreContainer>
          )}
        </ListContainer>
      </ScrollContainer>

      <NavigationModal
        showPrompt={archiveModalVisible}
        confirmNavigation={handleConfirmArchive}
        cancelNavigation={() => {
          setArchiveModalVisible(false)
          setSelectedReferral(null)
        }}
        modalTitle={
          selectedReferral?.status === 'active'
            ? t(referralConstants.ARCHIVE_REFERRAL)
            : t(referralConstants.RESTORE_REFERRAL)
        }
        modalContentText={
          selectedReferral?.status === 'active'
            ? t(referralConstants.ARCHIVE_CONFIRMATION)
            : t(referralConstants.RESTORE_CONFIRMATION)
        }
        modalConfirmText={
          selectedReferral?.status === 'active'
            ? t(referralConstants.ARCHIVE)
            : t(referralConstants.RESTORE)
        }
        modalCancelText={t(referralConstants.CANCEL)}
      />

      <NavigationModal
        showPrompt={showRemoveAttachmentModal}
        modalTitle="Remove Attachment"
        modalContentText={`Are you sure you want to remove "${attachmentToRemove?.filename}"? This action cannot be undone.`}
        modalConfirmText="Remove"
        modalCancelText="Cancel"
        confirmNavigation={handleConfirmRemoveAttachment}
        cancelNavigation={handleCancelRemoveAttachment}
      />
    </>
  )
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px auto 0px auto;
  padding-bottom: 40px;
  max-width: 1080px;
`

const SectionHeading = styled.div`
  margin-top: 32px;
  margin-bottom: 16px;
`

const ScrollContainer = styled.div`
  overflow-y: auto;
  height: 100%;
`

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`

const AttachmentLink = styled.a`
  font-size: 14px;
  color: ${({ theme }) => theme.color?.status?.popout || '#FF5887'};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const AttachmentIcon = styled.span`
  color: ${({ theme }) => theme.color?.status?.popout || '#FF5887'};
  font-size: 14px;
  margin-left: 8px;
`

const DocumentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color?.neutral?.color5 || '#8c8c8c'};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.color?.status?.danger || '#ff4d4f'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
